import React, { Fragment, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Box from "@mui/material/Box";

import SimpleTable from "../../../shared/components/common/ui/SimpleTable";
import HeaderPage from "../../../shared/components/HeaderPage/HeaderPage";
import NoData from "../../../shared/components/common/ui/NoData";

import { notify } from "../../../shared/utils/utils";
import { CampaignStatus } from "../../../services/api/Campaigns/CampaignStatus";
import { CampaignHidden } from "../../../services/api/Campaigns/CampaignHidden";

import { getCampaignColumns } from "./CampaignColumns";
import { getCampaignActions } from "./CampaignActions";
import { useCampaignsData } from "../hooks/useCampaignsData";

function CampaignPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const { campaigns, loading, isFetching, refetch } = useCampaignsData();

  useEffect(() => {
    if (location.state?.shouldRefetch) {
      refetch();
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, refetch, navigate, location.pathname]);

  // Alter visibility
  const alterVisibility = async (campaign_id, visibility) => {
    try {
      const response = await CampaignHidden({ campaign_id, visibility });

      if (response.message === "success") {
        notify(
          "Success",
          visibility
            ? "Campanha exibida com sucesso!"
            : "Campanha ocultada com sucesso!",
          true
        );

        refetch();
      }
    } catch (error) {
      console.error(error);
      notify("Error", "Erro ao alterar visibilidade", true);
    }
  };

  // Alter status
  const handleStatus = async (campaign_id, status, willActivate) => {
    try {
      const response = await CampaignStatus({ campaign_id, status });

      if (response.message === "success") {
        notify(
          "Success",
          willActivate
            ? "Campanha ativada com sucesso!"
            : "Campanha desativada com sucesso!",
          true
        );

        refetch();
      }
    } catch (error) {
      console.error(error);
      notify("Error", "Erro ao alterar status", true);
    }
  };

  const selectedOption = (campaign) => {
    const willActivate = campaign.status === "Desativado";

    if (
      !window.confirm(
        willActivate
          ? "Deseja ativar a campanha?"
          : "Deseja desativar a campanha?"
      )
    )
      return;

    handleStatus(
      campaign.id,
      willActivate ? "active" : "false",
      willActivate
    );
  };

  const selectCampaignToChangeVisibility = (campaign) => {
    const willHide = campaign.Visibility === "Visível";

    if (
      !window.confirm(
        willHide
          ? "Deseja ocultar a campanha?"
          : "Deseja exibir a campanha?"
      )
    )
      return;

    alterVisibility(campaign.id, !willHide);
  };

  const handleViewCampaign = (campaign) => {
    navigate(`/campanhas/${campaign.id}`, {
      state: { campaign },
    });
  };

  const handleEditCampaign = (campaign) => {
    navigate(`/campanhas/${campaign.id}/editar`, {
      state: { campaign },
    });
  };

  const formatarData = (data) => new Date(data).toLocaleDateString("pt-BR");

  const columns = useMemo(() => getCampaignColumns(formatarData), []);

  const actions = getCampaignActions(
    handleViewCampaign,
    handleEditCampaign,
    selectedOption,
    selectCampaignToChangeVisibility
  );

  return (
    <Fragment>
      <Box width="100%" minHeight="100vh">
        <HeaderPage
          variant="listing"
          title="Campanhas"
          description="Acompanhe, edite e organize as campanhas enviadas pela sua conta."
        />

        {!loading && !isFetching && campaigns?.length === 0 ? (
          <NoData
            title="Parece que você não tem campanhas :("
            description="Crie uma agora mesmo, é fácil e rápido"
            buttonText="Criar campanha"
            onClick={() => navigate("/campanhas/adicionar")}
          />
        ) : (
          <SimpleTable
            tableName="Campanhas"
            data={campaigns || []}
            loading={loading}
            isFetching={isFetching}
            columns={columns}
            actions={actions}
            redirect={() => navigate("/campanhas/adicionar")}
            redirectText="Adicionar Campanha"
            onRefresh={refetch}
            variant="listing"
          />
        )}
      </Box>
    </Fragment>
  );
}

export default CampaignPage;
